import { Alert } from '@/components/ui/alert';
import { toaster } from '@/components/ui/toaster';
import { useInvitationData, useInvitationStoreSelector } from '@/contexts/invitation-context';
import { usePrepareInvitation, useSendInvitation } from '@/hooks/actions';
import { lexicalJsonToPlainText, validateRequiredVariables } from '@/libs/utils/lexical-utils';
import { getApiErrorMessage } from '@/libs/utils/error-utils';
import { InvitationMethod } from '@/types/invitation';
import { AlertDescription, Button, Text, VStack } from '@chakra-ui/react';
import { TemplateType } from '@fluenceur/types';
import { InvitationMessageEditor, type AvailableVariable } from '@fluenceur/ui';
import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { MessageLanguageToggle } from './components';
import InvitationMessageCardSkeleton from './components/invitation-message-card-skeleton';
import {
  resolveInvitationEditorContent,
  resolveInvitationEditorKey,
} from './templates/invitation-editor-content';
import { useDetailsMessageWiring } from './templates/use-details-message-wiring';
import { isPlanLimitError } from '@/libs/plan-limit-refusal';

/** Closes the delivery step; `success` reports whether the send went through. */
type OnCloseHandler = (args: { success?: boolean }) => void;

/**
 * DM/comment branch of the mono delivery step. The channel is IMPOSED by the
 * mode chosen on the channel step (no longer derived from the server
 * preference): the mode drives the active template library, the prepared
 * template, the link-required validation (DM requires it; comments cannot carry
 * a clickable link) and the send channel. Prepare is still fetched to source the
 * templates (cache hit from the channel step).
 */
export const DmCommentDetailsView: React.FC<{
  onClose: OnCloseHandler;
  mode: 'dm' | 'comment';
}> = ({ onClose, mode }) => {
  const { t } = useTranslation();
  const { invitationData, sourceRecommendationId } = useInvitationData();
  const updateInvitationMethods = useInvitationStoreSelector(
    (state) => state.updateInvitationMethods
  );

  const isDm = mode === 'dm';

  // Active send mode driving which (channel, context) template library is loaded.
  const activeMethod = isDm ? InvitationMethod.DIRECT_MESSAGE : InvitationMethod.COMMENT;

  const {
    messageSnapshot,
    compiledMessage,
    setMessageSnapshot,
    setOriginalTemplate,
    resetMessage,
    setMessageLanguage,
    languageToggleEnabled,
    selectedLanguage,
    overrideLanguage,
    isEditorUntouched,
    templateFlow,
    buildEditorFooter,
    saveAsTemplatePanel,
    handleEditorChange,
    handleLanguageToggle,
  } = useDetailsMessageWiring(activeMethod);

  const { data: preparationData, isLoading: isLoadingPrepare } = usePrepareInvitation(
    invitationData?.invitationId as string,
    { enabled: !!invitationData?.invitationId, language: overrideLanguage }
  );

  useEffect(() => {
    if (preparationData) {
      updateInvitationMethods(preparationData);
    }
  }, [preparationData, updateInvitationMethods]);

  // When the library has at least one entry for the active pair, the selector
  // owns the body; the backend-prepared template is only the legacy fallback.
  const dmUsesLibraryTemplate = templateFlow.entries.length > 0;

  const getTemplateByType = (type: TemplateType) => {
    if (!invitationData?.invitationMethods?.templates) return null;
    return invitationData.invitationMethods.templates.find((template) => template.type === type);
  };

  const dmTemplate = getTemplateByType(TemplateType.IG_DM_MESSAGE_INVITATION);
  const commentTemplate = getTemplateByType(TemplateType.IG_COMMENT_REPLY_INVITATION);

  // The prepared template follows the imposed mode, with the other channel's
  // template as a last-resort fallback so the editor is never empty.
  const preferredTemplate = isDm ? dmTemplate || commentTemplate : commentTemplate || dmTemplate;

  const dmEditorKey = resolveInvitationEditorKey({
    language: preferredTemplate?.language,
    selectedEntryId: templateFlow.selectedEntryId,
  });
  const dmEditorContent = resolveInvitationEditorContent({
    isEditorUntouched,
    selectedEntryId: templateFlow.selectedEntryId,
    messageSnapshot,
    fallbackContent: preferredTemplate?.contentLexicalJson,
  });

  const availableVariables: AvailableVariable[] = useMemo(() => {
    if (!preferredTemplate?.variables) return [];
    return preferredTemplate.variables.map((variable) => ({
      key: variable.key,
      label: variable.label,
      labelKey: variable.labelKey,
      description: variable.description,
      descriptionKey: variable.descriptionKey,
      value: variable.value,
      required: variable.required,
    }));
  }, [preferredTemplate]);

  // Validate required variables in the message
  const validationResult = useMemo(() => {
    if (!messageSnapshot) {
      return { isValid: true, missingVariables: [] };
    }
    return validateRequiredVariables(messageSnapshot, availableVariables);
  }, [messageSnapshot, availableVariables]);

  // Initialize original template and message snapshot when template is loaded
  useEffect(() => {
    if (dmUsesLibraryTemplate) return;
    if (preferredTemplate?.contentLexicalJson && !messageSnapshot) {
      // Use the Lexical JSON prepared by the backend with variable chips
      const initialEditorState = preferredTemplate.contentLexicalJson;
      setOriginalTemplate(initialEditorState);
      setMessageSnapshot(initialEditorState);
    }
  }, [
    preferredTemplate,
    messageSnapshot,
    setOriginalTemplate,
    setMessageSnapshot,
    dmUsesLibraryTemplate,
  ]);

  const { mutateAsync: sendInvitation, isPending: isSendingInvitation } = useSendInvitation();
  // The API checks the outbound text before resolving its variable tokens.
  const message =
    compiledMessage || (messageSnapshot ? lexicalJsonToPlainText(messageSnapshot) : '');
  const isMessageTooShort = message.length < 10;

  const handleSendInvitation = useCallback(
    async (channel: InvitationMethod) => {
      if (isMessageTooShort) return;

      if (invitationData?.invitationId && messageSnapshot && message) {
        await sendInvitation(
          {
            invitationId: invitationData.invitationId,
            channel,
            message,
            messageSnapshot,
            // Brand template attribution; null for system defaults (omitted by the hook).
            templateId: templateFlow.selectedTemplateId,
            ...(overrideLanguage ? { messageLanguage: overrideLanguage } : {}),
            ...((invitationData.sourceRecommendationId ?? sourceRecommendationId)
              ? {
                  recommendationId: invitationData.sourceRecommendationId ?? sourceRecommendationId,
                }
              : {}),
          },
          {
            onSuccess: () => {
              onClose({ success: true });
            },
            onError: (error: Error) => {
              // A plan refusal opens the upsell from the API client; a toast would repeat it.
              if (isPlanLimitError(error)) return;
              console.error('Error sending invitation', error);
              toaster.create({
                title: t('common.error'),
                description: getApiErrorMessage(
                  error,
                  t,
                  'invitations.modal.invitationDetails.sendError'
                ),
                type: 'error',
                duration: 3000,
              });
            },
          }
        ).catch(() => undefined); // The mutation's onError already surfaces the refusal.
      } else {
        console.error('Missing invitationId or message for sending invitation');
        toaster.create({
          title: t('common.error'),
          description: t('invitations.modal.invitationDetails.sendError'),
          type: 'error',
          duration: 3000,
        });
      }
    },
    [
      sendInvitation,
      onClose,
      invitationData?.invitationId,
      invitationData?.sourceRecommendationId,
      sourceRecommendationId,
      messageSnapshot,
      message,
      isMessageTooShort,
      overrideLanguage,
      templateFlow.selectedTemplateId,
      t,
    ]
  );

  // A DM requires the link variable; a comment reply does not (links are not
  // clickable in comments), so its validation never blocks the send.
  const isValidationRequired = isDm && !validationResult.isValid;

  // No copy-link field here: the shareable link is the LINK mode's own surface
  // (the message carries it as a variable when the channel needs it).
  return (
    <VStack align="stretch" w="full" gap={4}>
      {isLoadingPrepare ? (
        <InvitationMessageCardSkeleton />
      ) : (
        <>
          {dmEditorContent && (
            <InvitationMessageEditor
              key={dmEditorKey}
              initialContent={dmEditorContent}
              availableVariables={availableVariables}
              onContentChange={handleEditorChange}
              onReset={resetMessage}
              labels={{
                title: t('invitations.editor.title'),
                reset: t('invitations.editor.reset'),
                placeholder: t('invitations.editor.placeholder'),
                insertVariable: t('invitations.editor.insertVariable'),
              }}
              translateFn={t}
              footerTrailing={buildEditorFooter(isLoadingPrepare)}
            />
          )}

          {isValidationRequired && (
            <Alert status="error" colorPalette="red" p={4} rounded="lg">
              <AlertDescription>
                <Text>
                  {t('invitations.editor.missingRequiredVariable', {
                    variable: `{{${validationResult.missingVariables[0]}}}`,
                  })}
                </Text>
              </AlertDescription>
            </Alert>
          )}
          {isMessageTooShort && (
            <Alert status="error" colorPalette="red" p={4} rounded="lg">
              <AlertDescription>{t('templates.form.errors.contentTooShort')}</AlertDescription>
            </Alert>
          )}
        </>
      )}

      <MessageLanguageToggle
        enabled={languageToggleEnabled}
        value={selectedLanguage}
        onToggle={handleLanguageToggle}
        onChange={setMessageLanguage}
        disabled={isLoadingPrepare}
      />

      <Button
        w="full"
        colorPalette="gray"
        onClick={() =>
          handleSendInvitation(isDm ? InvitationMethod.DIRECT_MESSAGE : InvitationMethod.COMMENT)
        }
        disabled={isLoadingPrepare || isValidationRequired || isMessageTooShort}
        loading={isSendingInvitation}
      >
        {isDm
          ? t('invitations.modal.invitationDetails.sendByDm')
          : t('invitations.modal.invitationDetails.replyAsComment')}
      </Button>

      {saveAsTemplatePanel}
    </VStack>
  );
};

export default DmCommentDetailsView;
